import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

interface AuthFormProps {
  mode: 'signin' | 'signup';
}

export function AuthForm({ mode }: AuthFormProps) {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showEmailConfirmOption, setShowEmailConfirmOption] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const {
    signIn,
    signUp,
    signInWithMagicLink,
    signInWithGoogle,
    isAuthenticated,
    resendConfirmationEmail
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (mode === 'signup') {
      const params = new URLSearchParams(location.search);
      const ref = params.get('ref');
      if (ref) setReferralCode(ref.toUpperCase());
    }
  }, [location.search, mode]);

  const validateEmail = (e: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const validatePassword = (p: string) => {
    const len = p.length >= 8;
    const upper = /[A-Z]/.test(p);
    const lower = /[a-z]/.test(p);
    const num = /[0-9]/.test(p);
    const spec = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p);
    return len && upper && lower && num && spec;
  };

  const getPasswordRequirements = (p: string): string[] => {
    const reqs: string[] = [];
    if (p.length < 8) reqs.push('pelo menos 8 caracteres');
    if (!/[A-Z]/.test(p)) reqs.push('pelo menos uma letra maiúscula');
    if (!/[a-z]/.test(p)) reqs.push('pelo menos uma letra minúscula');
    if (!/[0-9]/.test(p)) reqs.push('pelo menos um número');
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p)) reqs.push('pelo menos um caractere especial');
    return reqs;
  };

  const validateUsername = (u: string) =>
    u.length >= 3 && /^[a-zA-Z0-9_]+$/.test(u);

  const getErrorMessage = (err: any): string => {
    console.error('Erro detalhado:', err);
    if (err?.message?.includes('invalid_credentials'))
      return 'Email ou senha inválidos. Por favor, tente novamente.';
    if (err?.message?.includes('email') || err?.code === 'invalid_email')
      return 'Por favor, insira um endereço de email válido.';
    if (err?.message?.includes('username') || err?.message?.includes('nome de usuário'))
      return 'Este nome de usuário já está em uso. Por favor, escolha outro.';
    if (err?.code === 'user_already_exists' || err?.message?.includes('already registered'))
      return 'Este email já está cadastrado. Tente fazer login ou usar outro email.';
    if (err?.code === '23505')
      return 'Este email ou nome de usuário já está cadastrado.';
    if (err?.code === '429')
      return 'Muitas tentativas. Aguarde alguns instantes e tente novamente.';
    if (err?.code === 'service_unavailable' || err?.message?.includes('failed'))
      return 'Serviço temporariamente indisponível. Tente novamente mais tarde.';
    return err?.message || 'Ocorreu um erro ao processar sua solicitação.';
  };

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (loading) {
      timer = setTimeout(() => {
        setLoading(false);
        setError('A operação está demorando mais que o esperado. Tente novamente ou use o link mágico.');
        setUseMagicLink(true);
      }, 15000);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validateEmail(email)) {
      setError('Por favor, insira um endereço de email válido');
      return;
    }
    setLoading(true);
    try {
      const res = await signInWithMagicLink(email);
      res.success ? setMagicLinkSent(true) : setError(res.error || 'Erro ao enviar link de acesso');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await signInWithGoogle(referralCode);
      if (!res.success) setError(res.error || 'Erro ao fazer login com Google');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmationEmail = async () => {
    if (!email) {
      setError('Por favor, forneça um endereço de e-mail');
      return;
    }
    setResendingEmail(true);
    setError(null);
    setResendSuccess(false);
    try {
      const res = await resendConfirmationEmail(email);
      if (res.success) {
        setResendSuccess(true);
        setShowEmailConfirmOption(false);
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setError(res.error || 'Erro ao reenviar e-mail de confirmação');
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setShowEmailConfirmOption(false);
    setResendSuccess(false);

    if (!validateEmail(email)) {
      setError('Por favor, insira um endereço de email válido');
      return;
    }

    if (mode === 'signup') {
      if (!validatePassword(password)) {
        const missing = getPasswordRequirements(password);
        setError(`A senha deve conter ${missing.join(', ')}`);
        return;
      }
      if (password !== confirmPassword) {
        setError('As senhas não coincidem');
        return;
      }
      if (!validateUsername(username)) {
        setError('O nome de usuário deve ter pelo menos 3 caracteres e conter apenas letras, números e underscores');
        return;
      }
      if (!termsAccepted) {
        setError('Você precisa aceitar os Termos de Serviço e a Política de Privacidade para criar uma conta.');
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === 'signin') {
        const res = await signIn(email, password);
        if (res.success) {
          navigate('/');
        } else {
          if (res.isEmailConfirmationError) {
            setError('Por favor, confirme seu e-mail antes de fazer login. Verifique sua caixa de entrada e spam.');
            setShowEmailConfirmOption(true);
          } else if (
            res.error?.toLowerCase().includes('invalid') ||
            res.error?.toLowerCase().includes('credenciais')
          ) {
            setError('Por favor, confirme seu e-mail antes de fazer login. Verifique sua caixa de entrada e spam.');
            setShowEmailConfirmOption(true);
          } else {
            setError(res.error || 'Erro ao fazer login');
          }
        }
      } else {
        const res = await signUp(
          email,
          password,
          username,
          fullName,
          referralCode,
          termsAccepted,
          marketingConsent
        );
        if (res.success) {
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setUsername('');
          setFullName('');
          setReferralCode('');
          setTermsAccepted(false);
          setMarketingConsent(false);
          setEmailConfirmationSent(true);
        } else {
          setError(res.error || 'Erro ao criar conta');
        }
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // --- Renderizações condicionais ---

  if (emailConfirmationSent) {
    return (
      <div className="flex items-center justify-center overflow-visible py-8 px-4 sm:px-6 lg:px-8">
        <div className={`max-w-md w-full space-y-8 p-8 rounded-lg shadow-md overflow-visible ${
            theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white'
          }`}>
          <div className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h2 className={`mt-6 text-3xl font-extrabold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
              Confirme seu Email!
            </h2>
            <p className={`mt-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } break-words`}>
              Enviamos um link de confirmação para seu email.<br/>
              Por favor, verifique sua caixa de entrada e clique no link para ativar sua conta.
            </p>
            <div className={`mt-4 p-4 border rounded-md ${
                theme === 'dark' ? 'bg-yellow-700/20 border-yellow-500/30 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-700'
              }`}>
              <p className="text-sm font-medium">Importante:</p>
              <ul className="list-disc pl-5 text-sm mt-1 space-y-1">
                <li>O email pode demorar alguns minutos para chegar</li>
                <li>Verifique sua pasta de spam caso não encontre na caixa de entrada</li>
                <li>Você precisa confirmar seu email antes de poder fazer login</li>
              </ul>
            </div>

            {error && (
              <div className={`mt-4 border px-4 py-3 rounded flex items-center gap-2 ${
                  theme === 'dark' ? 'bg-destructive/20 border-destructive/30 text-destructive-foreground' : 'bg-red-50 border-red-200 text-red-600'
                }`}>
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="break-words">{error}</span>
              </div>
            )}

            {resendSuccess && (
              <div className={`mt-4 border px-4 py-3 rounded flex items-center gap-2 ${
                  theme === 'dark' ? 'bg-emerald-700/20 border-emerald-500/30 text-emerald-400' : 'bg-green-50 border-green-200 text-green-600'
                }`}>
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span>E-mail de confirmação reenviado com sucesso!</span>
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link to="/">Ir para Login</Link>
              </Button>
              <Button
                onClick={handleResendConfirmationEmail}
                disabled={resendingEmail}
                variant="outline"
                type="button"
              >
                {resendingEmail ? 'Reenviando...' : 'Reenviar E-mail'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (magicLinkSent) {
    return (
      <div className="flex items-center justify-center overflow-visible py-8 px-4 sm:px-6 lg:px-8">
        <div className={`max-w-md w-full space-y-8 p-8 rounded-lg shadow-md overflow-visible ${
            theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white'
          }`}>
          <div className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h2 className={`mt-6 text-3xl font-extrabold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
              Link Enviado!
            </h2>
            <p className={`mt-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } break-words`}>
              Enviamos um link de acesso para seu email.<br/>
              Por favor, verifique sua caixa de entrada e clique no link para entrar.
            </p>
            <div className={`mt-4 p-4 border rounded-md ${
                theme === 'dark' ? 'bg-yellow-700/20 border-yellow-500/30 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-700'
              }`}>
              <p className="text-sm font-medium">Observação:</p>
              <ul className="list-disc pl-5 text-sm mt-1 space-y-1">
                <li>O email pode demorar alguns minutos para chegar</li>
                <li>Caso não encontre na caixa de entrada, verifique o spam</li>
              </ul>
            </div>
            <Button onClick={() => setMagicLinkSent(false)} className="mt-4">
              Voltar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex items-center justify-center overflow-visible py-8 px-4 sm:px-6 lg:px-8">
        <div className={`max-w-md w-full space-y-8 p-8 rounded-lg shadow-md overflow-visible ${
            theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white'
          }`}>
          <div className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h2 className={`mt-6 text-3xl font-extrabold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
              Conta Criada!
            </h2>
            <p className={`mt-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } break-words`}>
              Sua conta foi criada com sucesso. Agora você pode fazer login.
            </p>
            <Button asChild className="mt-4">
              <Link to="/signin">Ir para Login</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (useMagicLink && mode === 'signin') {
    return (
      <div className="flex items-center justify-center overflow-visible py-8 px-4 sm:px-6 lg:px-8">
        <div className={`max-w-md w-full space-y-8 p-8 rounded-lg shadow-md overflow-visible ${
            theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white'
          }`}>
          <div>
            <h2 className={`mt-6 text-center text-3xl font-extrabold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
              Login com Link Mágico
            </h2>
            <p className={`mt-2 text-center text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } break-words`}>
              Enviaremos um link para seu email que permite entrar sem senha
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleMagicLinkSubmit} noValidate>
            {error && (
              <div className={`border px-4 py-3 rounded flex items-center gap-2 ${
                  theme === 'dark' ? 'bg-destructive/20 border-destructive/30 text-destructive-foreground' : 'bg-red-50 border-red-200 text-red-600'
                }`}>
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="break-words">{error}</span>
              </div>
            )}

            <div>
              <Label htmlFor="email-address" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Email
              </Label>
              <Input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com"
              />
            </div>

            <div>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Processando...' : 'Enviar Link de Acesso'}
              </Button>
            </div>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                onClick={() => setUseMagicLink(false)}
                className={`${theme === 'dark' ? 'text-primary' : 'text-indigo-600 hover:text-indigo-500'}`}
              >
                Voltar para login com senha
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // --- Renderização padrão ---
  return (
    <div className="flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className={`max-w-md w-full space-y-4 p-8 rounded-lg shadow-md overflow-visible ${
          theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white'
        }`}>
        <div>
          <h2 className={`mt-6 text-center text-3xl font-extrabold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            {mode === 'signin' ? 'Entrar na sua conta' : 'Criar sua conta'}
          </h2>
          {mode === 'signup' && (
            <p className={`mt-2 text-center text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } break-words`}>
              A senha deve ter pelo menos 6 caracteres
            </p>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignIn}
          disabled={loading || resendingEmail}
          className="w-full"
        >
          {/* Ícone do Google */}
          {mode === 'signin' ? 'Entrar com Google' : 'Cadastrar com Google'}
        </Button>

        <div className="mt-2 flex items-center justify-between">
          <div className={`flex-grow border-t ${
              theme === 'dark' ? 'border-border' : 'border-gray-300'
            }`} />
          <span className={`flex-shrink mx-4 text-sm ${
              theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'
            }`}>ou</span>
          <div className={`flex-grow border-t ${
              theme === 'dark' ? 'border-border' : 'border-gray-300'
            }`} />
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className={`border px-4 py-3 rounded flex flex-col gap-2 ${
                theme === 'dark' ? 'bg-destructive/10 border-destructive/30 text-destructive-foreground' : 'bg-red-50 border-red-200 text-red-600'
              }`}>
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mr-2" />
                <span className="break-words">{error}</span>
              </div>
              {showEmailConfirmOption && mode === 'signin' && (
                <div className={`mt-2 pt-2 ${
                    theme === 'dark' ? 'border-t border-destructive/30' : 'border-t border-red-200'
                  }`}>
                  <p className={`text-sm mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Não recebeu o e-mail de confirmação? Podemos enviar novamente:
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResendConfirmationEmail}
                    disabled={resendingEmail}
                  >
                    {resendingEmail ? 'Reenviando...' : 'Reenviar E-mail de Confirmação'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {resendSuccess && (
            <div className={`border px-4 py-3 rounded flex items-center gap-2 ${
                theme === 'dark' ? 'bg-emerald-700/20 border-emerald-500/30 text-emerald-400' : 'bg-green-50 border-green-200 text-green-600'
              }`}>
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <span>E-mail de confirmação reenviado com sucesso!</span>
            </div>
          )}

          {mode === 'signup' && (
            <>
              <div>
                <Label htmlFor="username" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nome de Usuário
                </Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="nomeusuario"
                />
              </div>

              <div>
                <Label htmlFor="fullName" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nome Completo
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Seu Nome Completo"
                />
              </div>
            </>
          )}

          <div>
            <Label htmlFor="email-address" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Email
            </Label>
            <Input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="seu@email.com"
            />
          </div>

          <div>
            <Label htmlFor="password" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Senha
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Sua senha"
            />
            {mode === 'signup' && (
              <div className={`mt-2 p-2 rounded-md ${
                  theme === 'dark' ? 'bg-sky-700/20' : 'bg-blue-50'
                }`}>
                <p className={`text-xs font-medium mb-1 ${
                    theme === 'dark' ? 'text-sky-300' : 'text-blue-700'
                  }`}>A senha deve conter:</p>
                <ul className={`text-xs pl-2 space-y-1 ${
                    theme === 'dark' ? 'text-sky-400' : 'text-blue-600'
                  }`}>
                  <li className={`flex items-center ${
                      password.length >= 8 ? 'text-green-600' : ''
                    }`}>
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                        password.length >= 8 ? 'bg-green-500' : 'bg-blue-300'
                      }`} />
                    Pelo menos 8 caracteres
                  </li>
                  <li className={`flex items-center ${
                      /[A-Z]/.test(password) ? 'text-green-600' : ''
                    }`}>
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                        /[A-Z]/.test(password) ? 'bg-green-500' : 'bg-blue-300'
                      }`} />
                    Pelo menos uma letra maiúscula
                  </li>
                  <li className={`flex items-center ${
                      /[a-z]/.test(password) ? 'text-green-600' : ''
                    }`}>
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                        /[a-z]/.test(password) ? 'bg-green-500' : 'bg-blue-300'
                      }`} />
                    Pelo menos uma letra minúscula
                  </li>
                  <li className={`flex items-center ${
                      /[0-9]/.test(password) ? 'text-green-600' : ''
                    }`}>
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                        /[0-9]/.test(password) ? 'bg-green-500' : 'bg-blue-300'
                      }`} />
                    Pelo menos um número
                  </li>
                  <li className={`flex items-center ${
                      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? 'text-green-600' : ''
                    }`}>
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? 'bg-green-500' : 'bg-blue-300'
                      }`} />
                    Pelo menos um caractere especial
                  </li>
                </ul>
              </div>
            )}
          </div>

          {mode === 'signup' && (
            <div>
              <Label htmlFor="confirm-password" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Confirmar Senha
              </Label>
              <Input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua senha"
              />
            </div>
          )}

          {mode === 'signup' && (
            <div className="mt-6 space-y-3">
              <div className="flex items-start">
                <Checkbox
                  id="terms-accepted"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted as (c: boolean) => void}
                />
                <div className="ml-3 text-sm">
                  <Label htmlFor="terms-accepted" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} break-words`}>
                    Eu li e aceito os{' '}
                    <Link to="/terms-of-service" target="_blank" className={`${theme === 'dark' ? 'text-primary hover:underline' : 'text-indigo-600 hover:text-indigo-500 underline'}`}>
                      Termos de Serviço
                    </Link>{' '}
                    e a{' '}
                    <Link to="/privacy-policy" target="_blank" className={`${theme === 'dark' ? 'text-primary hover:underline' : 'text-indigo-600 hover:text-indigo-500 underline'}`}>
                      Política de Privacidade
                    </Link>
                    <span className="text-red-500">*</span>
                  </Label>
                </div>
              </div>

              <div className="flex items-start">
                <Checkbox
                  id="marketing-consent"
                  checked={marketingConsent}
                  onCheckedChange={setMarketingConsent as (c: boolean) => void}
                />
                <div className="ml-3 text-sm">
                  <Label htmlFor="marketing-consent" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Eu concordo em receber comunicações de marketing e novidades (Opcional)
                  </Label>
                </div>
              </div>
            </div>
          )}

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Processando...' : mode === 'signin' ? 'Entrar' : 'Criar Conta'}
            </Button>
          </div>

          {mode === 'signin' && (
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                onClick={() => setUseMagicLink(true)}
                className={`${theme === 'dark' ? 'text-primary' : 'text-indigo-600 hover:text-indigo-500'}`}
              >
                Entrar com link mágico (sem senha)
              </Button>
            </div>
          )}

          <div className="text-center text-sm">
            {mode === 'signin' ? (
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} break-words`}>
                Não tem uma conta?{' '}
                <Link to="/signup" className={`${theme === 'dark' ? 'text-primary hover:underline' : 'text-indigo-600 hover:text-indigo-500'}`}>
                  Cadastre-se
                </Link>
              </p>
            ) : (
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} break-words`}>
                Já tem uma conta?{' '}
                <Link to="/signin" className={`${theme === 'dark' ? 'text-primary hover:underline' : 'text-indigo-600 hover:text-indigo-500'}`}>
                  Entre
                </Link>
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
